import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/_atoms/checkbox";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/_atoms/command";
import { cn } from "@/lib/cn";
import i18n from "@/lib/i18n";

import { useDatatableContext } from "../use-datatable-context";

import { FilterByIdListPayload, FilterByIdListPayloadSchema } from "./functions/filter-by-id-list";

const FINISH_DELAY_MS = 1;

export interface FilterByIdListItem {
  id: string;
  name: string;
}

export interface FilterByIdListConfigurationViewProps<TItem extends FilterByIdListItem> {
  columnId: string;
  itemList: TItem[];
  renderItem: (item: TItem | null, view: "icon" | "text" | "icon-text") => ReactElement;
  onFinished: () => void;
  hideNullOption?: boolean;
}

export const FilterByIdListConfigurationView = <TItem extends FilterByIdListItem>({
  onFinished,
  itemList,
  renderItem,
  columnId,
  hideNullOption,
}: FilterByIdListConfigurationViewProps<TItem>) => {
  const { t } = useTranslation("common");
  const { table, state, getConfigurationForColumn } = useDatatableContext();
  const { columnFilters } = state;

  const columnConfiguration = getConfigurationForColumn(columnId);

  const [selectedItemList, setSelectedItemList] = useState<(FilterByIdListItem | null)[] | null>(null);
  const isUpdatingFilterRef = useRef(false);
  const lastProcessedSelectionRef = useRef<string | null>(null);

  const column = table.getColumn(columnId);

  const filterValue = useMemo(() => {
    const filterValue = column?.getFilterValue();

    const safePayload = FilterByIdListPayloadSchema.safeParse(filterValue);
    if (!safePayload.success) {
      return null;
    }

    return safePayload.data;

    // We also listen to the columnFilters to update the filter value when the user changes the filter mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, columnFilters]);

  useEffect(() => {
    if (!filterValue) {
      return;
    }

    // we expect the filter-by-list to contain the ids of the items not the display values
    const newSelectedItemList = filterValue.idList.flatMap((id) => {
      if (id === null) {
        return [null];
      }
      const foundItem = itemList.find((listItem) => listItem.id === id);
      return foundItem ? [{ id: foundItem.id, name: foundItem.name }] : [];
    });

    const newSelectionString = JSON.stringify(newSelectedItemList.map((item) => item?.id ?? null).sort());

    // Skip if we've already processed this selection (prevents re-syncing when we update the filter)
    if (lastProcessedSelectionRef.current === newSelectionString) {
      return;
    }

    isUpdatingFilterRef.current = true;
    lastProcessedSelectionRef.current = newSelectionString;
    setSelectedItemList(newSelectedItemList);
    // Reset flag after a microtask
    Promise.resolve().then(() => {
      isUpdatingFilterRef.current = false;
    });
  }, [filterValue, itemList]);

  useEffect(() => {
    // Skip if we're updating the filter ourselves to prevent infinite loops
    if (isUpdatingFilterRef.current) {
      return;
    }

    if (!selectedItemList) {
      return;
    }

    // Create a stable string representation of the current selection
    const currentSelectionString = JSON.stringify(selectedItemList.map((item) => item?.id ?? null).sort());

    // Skip if we've already processed this exact selection
    if (lastProcessedSelectionRef.current === currentSelectionString) {
      return;
    }

    if (selectedItemList.length === 0) {
      isUpdatingFilterRef.current = true;
      lastProcessedSelectionRef.current = currentSelectionString;
      clearFilter();
      // Reset flag after a microtask to allow React to process the update
      Promise.resolve().then(() => {
        isUpdatingFilterRef.current = false;
      });
      return;
    }

    const newFilterValue: FilterByIdListPayload = {
      type: "filter_by_id_list",
      mode: filterValue?.mode ?? "included",
      idList: selectedItemList.map((item) => item?.id ?? null),
    };

    let shouldUpdate = false;
    if (filterValue?.mode !== newFilterValue.mode) {
      shouldUpdate = true;
    }

    const currentIdList = filterValue?.idList ? [...filterValue.idList].sort() : [];
    const newIdList = [...newFilterValue.idList].sort();
    const idListStr1 = JSON.stringify(currentIdList);
    const idListStr2 = JSON.stringify(newIdList);
    if (idListStr1 !== idListStr2) {
      shouldUpdate = true;
    }

    if (!shouldUpdate) {
      lastProcessedSelectionRef.current = currentSelectionString;
      return;
    }

    isUpdatingFilterRef.current = true;
    lastProcessedSelectionRef.current = currentSelectionString;
    // IMPORTANT: We filter by the id of the item not the name
    setFilter(newFilterValue);
    // Reset flag after a microtask to allow React to process the update
    Promise.resolve().then(() => {
      isUpdatingFilterRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemList]);

  const setFilter = (payload: FilterByIdListPayload) => {
    column?.setFilterValue(payload);
  };

  const clearFilter = () => {
    if (!column) {
      return;
    }
    table.setColumnFilters(columnFilters.slice().filter((filterValue) => filterValue.id !== column.id));
  };

  const handleSelect = useCallback(
    (item: FilterByIdListItem | null, shouldFinish?: boolean) => {
      // Skip if we're updating the filter to prevent re-triggering
      if (isUpdatingFilterRef.current) {
        return;
      }

      setSelectedItemList((prevList) => {
        const itemList = prevList ?? [];
        const updatedItemList = itemList.slice().filter((i) => i?.id !== item?.id); // avoid duplicates
        return [...updatedItemList, item];
      });

      if (shouldFinish) {
        setTimeout(function () {
          onFinished();
        }, FINISH_DELAY_MS);
      }
    },
    [onFinished],
  );

  const handleDeselect = useCallback(
    (item: FilterByIdListItem | null, shouldFinish?: boolean) => {
      // Skip if we're updating the filter to prevent re-triggering
      if (isUpdatingFilterRef.current) {
        return;
      }

      setSelectedItemList((prevList) => {
        const itemList = prevList ?? [];
        return itemList.slice().filter((i) => i?.id !== item?.id);
      });

      if (shouldFinish) {
        setTimeout(function () {
          onFinished();
        }, FINISH_DELAY_MS);
      }
    },
    [onFinished],
  );

  return (
    <Command className="h-full">
      <CommandInput placeholder={columnConfiguration?.getDisplayValue() ?? i18n.t("view.filterByIdList", { ns: "common" })} />
      <CommandList className="max-h-[300px]">
        <CommandGroup>
          {(hideNullOption ? [] : [null]).map((_) => {
            const isSelected = !!selectedItemList && selectedItemList.includes(null);
            const handleAction = isSelected ? handleDeselect : handleSelect;

            return (
              <CommandItem
                key={null}
                value={t("notAssigned")}
                className="group flex flex-row items-center gap-1 p-1 py-1.5 hover:cursor-pointer"
                onSelect={() => {
                  handleAction(null, true);
                }}
              >
                <Checkbox
                  className={cn([
                    "rounded-md border border-input group-hover:opacity-100 group-focus-within:opacity-100",
                    isSelected ? "" : " opacity-0",
                  ])}
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(null);
                  }}
                />
                <div className="pl-2">{renderItem(null, "icon-text")}</div>
              </CommandItem>
            );
          })}

          {itemList.map((item) => {
            const isSelected = !!selectedItemList && !!selectedItemList.find((i) => i?.id === item.id);
            const handleAction = isSelected ? handleDeselect : handleSelect;

            return (
              <CommandItem
                key={item.id}
                value={item.name}
                className="group flex flex-row items-center gap-1 p-1 py-1.5 hover:cursor-pointer"
                onSelect={() => {
                  handleAction(item, true);
                }}
              >
                <Checkbox
                  className={cn([
                    "rounded-md border border-input group-hover:opacity-100 group-focus-within:opacity-100",
                    isSelected ? "" : " opacity-0",
                  ])}
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(item);
                  }}
                />
                <div className="pl-2">{renderItem(item, "icon-text")}</div>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};
